<template>
  <div class="history">
    <div class="change-password" v-if="$store.state.change_password">
      <ChangePassword />
    </div>
    <div class="wrapper">
      <Navigation />
    </div>
    <SuperPassTimer v-if="currentPassOrder" :order="currentPassOrder" />

    <div v-else class="history-content">
      <Loader v-if="loading" />
      <Button v-if="currentTab !== 'superPass'" size="md" @click="goToTransactions">
        {{ $t('history.pendingInfo') }}
      </Button>
      <div class="history-content__header">
        <div
          class="history-content__header__block"
          :class="{ active: currentTab === 'transfer' }"
          @click="currentTab = 'transfer'"
        >
          <p>{{ $t('general.transfer') }}</p>
          <small>{{ totalOrders.transfer }}</small>
        </div>
        <div class="history-content__header__block" :class="{ active: isGifts }" @click="setTab('gifts')">
          <p>{{ giftTitle }}</p>
          <small>{{ totalOrders.gifts }}</small>
        </div>
        <div
          class="history-content__header__block"
          :class="{ active: currentTab === 'superPass' }"
          @click="setTab('superPass')"
        >
          <p>{{ $t('general.superPass') }}</p>
          <small>{{ totalOrders.superPass }}</small>
        </div>
        <div
          class="history-content__header__block"
          :class="{ active: currentTab === 'packs' }"
          @click="setTab('packs')"
        >
          <p>{{ $t('general.packs') }}</p>
          <small>{{ totalOrders.packs }}</small>
        </div>
      </div>

      <!-- Table -->
      <div class="history-table">
        <Loader v-if="ordersLoading" />
        <!-- Header -->
        <div v-if="isBaseTab || isGifts" class="history-table__header" :class="{ isGift: isGifts }">
          <p class="id">ID</p>
          <p v-if="!isGifts" class="nickname">{{ $t('history.nickname') }}</p>
          <p class="sum-robux">{{ $t('history.sumRobux') }}</p>
          <p class="sum-rub">{{ $t('history.sumRub') }}</p>
          <p class="status">{{ $t('history.status') }}</p>
          <p class="description">{{ $t('history.description') }}</p>
          <p class="timer">{{ $t('history.time') }}</p>
        </div>
        <div
          v-if="currentTab === 'superPass'"
          class="history-table__header"
          :class="[`history-table__header--${currentTab}`]"
        >
          <p class="id">ID</p>
          <p class="nickname">{{ $t('history.nickname') }}</p>
          <p class="sum-robux">{{ $t('history.game') }}</p>
          <p class="sum-robux">{{ $t('history.pass') }}</p>
          <p class="sum-rub">{{ $t('history.sumRub') }}</p>
          <p class="status">{{ $t('history.status') }}</p>
          <p class="description">{{ $t('history.description') }}</p>
          <p class="timer">{{ $t('history.time') }}</p>
        </div>
        <div v-if="currentTab === 'packs'" class="history-table__header history-table__header--packs">
          <p class="id">ID</p>
          <p class="nickname">{{ $t('history.nickname') }}</p>
          <p class="sum-robux">{{ $t('history.sumRobux') }}</p>
          <p class="sum-rub">{{ $t('history.sumRub') }}</p>
          <p class="status">{{ $t('history.status') }}</p>
          <p class="timer">{{ $t('history.time') }}</p>
        </div>

        <!-- Rows -->
        <div class="history-table__rows">
          <template v-if="isBaseTab">
            <div v-for="order in currentOrders" :key="order._id" class="history-table__row" :class="{ isGift: isGifts }">
              <p class="id">
                <span>{{ order._id.slice(-4) }}</span>
                <Button class="history-table__copy" left-icon="/base/copy.svg" @click="copyId(order._id)" size="sm">
                  id
                </Button>
              </p>
              <p v-if="order.gift_code && !isGifts" class="nickname">Подарочная карта</p>
              <p v-else-if="!isGifts" class="nickname">{{ order.username }}</p>
              <p class="sum-robux">
                <template v-if="order.promo">
                  <span>{{ order.promo.robux_amount_no_promo }} R$</span>
                  <span v-if="order.promo" class="promo">+ {{ getPromoBonus(order.promo) }} R$</span>
                </template>
                <template v-else>
                  <span>{{ order.robux_amount }} R$</span>
                </template>
              </p>
              <p v-if="order.currency" class="sum-rub">
                {{ getOrderTotal(order) }}
                {{ $store.getters.getCurrencySign(order.currency.toLowerCase()) }}
              </p>
              <p v-else class="sum-rub">{{ order.amount[$store.state.currency].toFixed(2) }} ₽</p>
              <p class="status">
                <template v-if="!isGifts">
                  <span class="error" v-if="order.status === 'error' || order.status === 'system_error'">
                    {{ $t('history.error') }}
                  </span>
                  <span class="waiting" v-if="['waiting', 'paid', 'code', 'robuxship'].includes(order.status)">
                    {{ $t('history.wait') }}
                  </span>
                  <span class="success" v-if="order.status === 'success'">
                    {{ $t('history.success') }}
                  </span>
                  <template v-if="order.gift_code">
                    <span class="success">
                      {{ $t('history.giftInfo') }}
                    </span>
                  </template>
                </template>
                <span v-else-if="order.gift_code" class="success">{{ $t('history.giftInfo') }}</span>
                <span v-else>—</span>
              </p>
              <p v-if="!order.gift_code" class="description">
                {{ getOrderDescription(order) || '—' }}
                <template v-if="order.status === 'robuxship'">
                  <br />
                  {{ $t('transfer.timeIncreased') }}
                </template>
              </p>
              <template v-else>
                <p class="description gift">
                  {{ $t('order.descriptions.gift') }}
                  <a class="gift-open" @click="showGiftModal(order)">
                    {{ $t('actions.open') }}
                  </a>
                </p>
              </template>

              <p v-if="currentTab === 'transfer'" class="timer">{{ parseDate(order._id, 'orders') }}</p>
              <p v-else class="timer">{{ parseDate(order._id, 'giftOrders') }}</p>
            </div>
          </template>
          <template v-if="isGifts">
            <div v-for="order in keyOrders" :key="order._id" class="history-table__row isGift">
              <p class="id">
                <span>{{ order._id.slice(-4) }}</span>
                <Button class="history-table__copy" left-icon="/base/copy.svg" @click="copyId(order._id)" size="sm">
                  id
                </Button>
              </p>
              <p class="sum-robux">
                <span>{{ order.quantity }} R$</span>
              </p>
              <p class="sum-rub">
                {{ order.amount && order.amount[$store.state.currency] ? order.amount[$store.state.currency].toFixed(2) : '—' }}
                {{ $store.getters.getCurrencySign($store.state.currency) }}
              </p>
              <p class="status">
                <span class="error" v-if="order.status === 'error' || order.status === 'system_error'">
                  {{ $t('history.error') }}
                </span>
                <span class="waiting" v-if="['waiting', 'paid', 'code'].includes(order.status)">
                  {{ $t('history.wait') }}
                </span>
                <span class="success" v-if="order.status === 'success'">
                  {{ $t('history.success') }}
                </span>
              </p>
              <template v-if="order.key || order.code">
                <p class="description gift">
                  {{ $t('order.descriptions.gift') }}
                  <a class="gift-open" @click="showKeyModal(order)">
                    {{ $t('actions.open') }}
                  </a>
                </p>
              </template>
              <p v-else class="description">—</p>
              <p class="timer">{{ parseDate(order._id, 'keyOrders') }}</p>
            </div>
          </template>
          <template v-if="currentTab === 'superPass'">
            <div
              v-for="order in passOrders"
              :key="order._id"
              class="history-table__row"
              :class="[`history-table__row--${currentTab}`]"
            >
              <p class="id">
                <span>{{ order._id.slice(-4) }}</span>
                <Button class="history-table__copy" left-icon="/base/copy.svg" @click="copyId(order._id)" size="sm">
                  id
                </Button>
              </p>
              <p class="nickname">{{ order.username }}</p>
              <p class="nickname">{{ getGameInfo(order).gameName }}</p>
              <p class="nickname">{{ getGameInfo(order).passName }}</p>
              <p class="sum-rub">
                {{ getOrderTotal(order) }} {{ $store.getters.getCurrencySign(order.currency.toLowerCase()) }}
              </p>
              <p class="status">
                <span
                  class="error"
                  v-if="order.status === 'error' || order.status === 'system_error' || order.status === 'cancel'"
                >
                  {{ $t('history.error') }}
                </span>
                <span class="error pass" v-if="order.status === 'in_game'" @click="scrollToTop">
                  {{ $t('history.passClick') }}
                </span>
                <span class="waiting" v-if="['waiting', 'paid', 'code', 'robuxship'].includes(order.status)">
                  {{ $t('history.wait') }}
                </span>
                <span class="success" v-if="order.status === 'success'">
                  {{ $t('history.success') }}
                </span>
              </p>
              <p class="description" :class="{ error: order.status === 'in_game' }">
                {{ getOrderPassDescription(order) }}
              </p>
              <p class="timer">{{ parseDate(order._id, 'passOrders') }}</p>
            </div>
          </template>
          <template v-if="currentTab === 'packs'">
            <div v-if="!packOrders.length" class="history-table__row">
              <p class="description">{{ $t('packs.history.empty') }}</p>
            </div>
            <div v-for="order in packOrders" :key="order._id" class="history-table__row history-table__row--packs">
              <p class="id">
                <span>{{ order._id.slice(-4) }}</span>
                <Button class="history-table__copy" left-icon="/base/copy.svg" @click="copyId(order._id)" size="sm">
                  id
                </Button>
              </p>
              <p class="nickname">{{ order.username }}</p>
              <p class="sum-robux">
                <span>{{ getPackProductName(order) }}</span>
              </p>
              <p class="sum-rub">
                {{ getPackAmount(order) }}
                {{ $store.getters.getCurrencySign('rub') }}
              </p>
              <p class="status">
                <!-- cancel: деньги уже вернулись на баланс, показываем причину -->
                <span
                  class="error"
                  v-if="isPackError(order)"
                  :title="order.cancel_reason || ''"
                  @click="showPackCancelReason(order)"
                >
                  {{ $t('history.error') }}
                </span>
                <!-- verification: Roblox просит подтвердить игры — открываем модалку -->
                <span
                  class="action"
                  v-else-if="order.status === PACK_STATUS.VERIFICATION && order.verification_url"
                  @click="openPackVerification(order)"
                >
                  {{ $t('packs.history.verification') }}
                </span>
                <!-- оплачен, но данные аккаунта ещё не переданы -->
                <span class="action" v-else-if="isPackWaitingCredentials(order)" @click="openPacksWizard(order)">
                  {{ $t('packs.history.actionRequired') }}
                </span>
                <span class="waiting" v-else-if="order.status === PACK_STATUS.NEW">
                  {{ $t('packs.history.waitingPayment') }}
                </span>
                <span
                  class="waiting"
                  v-else-if="[PACK_STATUS.PAID, PACK_STATUS.PROCESSING, PACK_STATUS.VERIFICATION].includes(order.status)"
                >
                  {{ $t('history.wait') }}
                </span>
                <span class="success" v-else-if="order.status === PACK_STATUS.SUCCESS">
                  {{ $t('history.success') }}
                </span>
                <span v-else>—</span>
              </p>
              <p class="timer">{{ parseDate(order._id, 'packOrders') }}</p>
            </div>
          </template>
        </div>
      </div>
      <!-- Footer -->
      <div class="history-footer" v-show="totalOrders[currentTab] > 0">
        <div class="history-footer__nav">
          <div
            class="history-footer__nav__left"
            v-on="currentPage - 1 <= 0 ? {} : { click: () => changeOrders(currentPage - 1, currentTab) }"
          >
            <img src="./../../assets/images/history/left-arrow.svg" alt="left" />
          </div>
          <div class="history-footer__nav__center">
            <p
              v-for="i in currentPaginationPages"
              :key="i"
              :class="{ active: i === currentPage }"
              @click="changeOrders(i, currentTab)"
            >
              {{ i }}
            </p>
            <p v-if="showEllipsisBefore">...</p>
            <p v-if="showLastPage" @click="currentPage = totalPages[currentTab]">
              {{ totalPages[currentTab] }}
            </p>
          </div>
          <div
            class="history-footer__nav__right"
            v-on="
              currentPage + 1 > totalPages[currentTab] ? {} : { click: () => changeOrders(currentPage + 1, currentTab) }
            "
          >
            <img src="./../../assets/images/history/right-arrow.svg" alt="right" />
          </div>
        </div>
        <div class="history-footer__info">
          <i18n path="history.orderCount" tag="p">
            <template v-slot:order>
              <span v-if="currentTab === 'transfer'">{{ orders.length }}</span>
              <span v-if="isGifts">{{ keyOrders.length }}</span>
              <span v-if="currentTab === 'superPass'">{{ passOrders.length }}</span>
              <span v-if="currentTab === 'packs'">{{ packOrders.length }}</span>
            </template>

            <template v-slot:length>
              {{ totalOrders[currentTab] }}
            </template>
          </i18n>
        </div>
      </div>
    </div>
    <!-- Modal -->
    <GiftModal
      v-if="isShowGiftModal"
      :gift-code="gift.code"
      :robux="gift.robux"
      :instruction="gift.instruction"
      @close="isShowGiftModal = false"
    />

    <!-- Робукс паки: модалка выдачи (данные аккаунта) после оплаты, как на дереве -->
    <PacksWizard
      v-if="isShowPacksWizard"
      :order="activePackOrder"
      @close="isShowPacksWizard = false"
      @done="onPacksWizardDone"
    />
    <PacksProcessing v-if="isShowPacksProcessing" @close="onPacksProcessingClose" />
    <!-- Провайдер запросил проверку: без неё заказ не выполнится -->
    <PacksVerification
      v-if="isShowPacksVerification"
      :order="packVerificationOrder"
      @close="onPacksVerificationClose"
    />
  </div>
</template>

<script>
import './history.scss'

import Navigation from '@/components/Navigation/Navigation'
import ChangePassword from '@/components/ChangePassword/ChangePassword'
import Loader from '@/components/LoaderBar/LoaderBar'
import Button from '@/components/Base/Button/Button.vue'
import GiftModal from '@/components/Modals/Gift/GiftModal.vue'
import PacksWizard from '@/components/Packs/PacksWizard/PacksWizard.vue'
import PacksProcessing from '@/components/Packs/PacksProcessing/PacksProcessing.vue'
import PacksVerification from '@/components/Packs/PacksVerification/PacksVerification.vue'
import SuperPassTimer from '@/components/SuperPass/superPassTimer/superPassTimer.vue'
import { getPromoBonus } from '@/utils/promo'
import { copyText } from '@/utils/copyText'
import {
  getPacksHistory,
  isPackWaitingCredentials,
  PACK_ACTIVE_STATUSES,
  PACK_STATUS
} from '@/utils/packsApi'
import windowSize from '@/mixins/windowSize'
import ScreenSize from '@/enums/ScreenSize'

const PROMO_CODE_ERROR = 8
const CODE_WITH_SELF_DESCRIPTION = [PROMO_CODE_ERROR]

// Заказы паков выполняются асинхронно у провайдера — опрашиваем статус.
const PACKS_POLL_INTERVAL = 5000

export default {
  name: 'History',
  components: {
    SuperPassTimer,
    GiftModal,
    PacksWizard,
    PacksProcessing,
    PacksVerification,
    Button,
    Navigation,
    ChangePassword,
    Loader
  },
  inject: ['currencyMap'],
  mixins: [windowSize],
  data() {
    return {
      loading: true,
      ordersLoading: false,
      totalPages: {
        transfer: 1,
        gifts: 1,
        superPass: 1,
        packs: 1
      },
      totalOrders: {
        transfer: 0,
        gifts: 0,
        keys: 0,
        superPass: 0,
        packs: 0
      },
      currentPage: 1,
      orders: [],
      giftOrders: [],
      keyOrders: [],
      passOrders: [],
      packOrders: [],
      timers: [],
      games: [],
      passes: [],
      isShowGiftModal: false,
      isShowPacksWizard: false,
      isShowPacksProcessing: false,
      activePackOrder: null,
      packsStatusIntervalId: null,
      isShowPacksVerification: false,
      packVerificationOrder: null,
      // id заказов, по которым модалку проверки уже закрыли — опрос идёт каждые
      // 5 секунд, и без этого она открывалась бы снова
      closedVerifications: [],
      currentPassOrder: null,
      passStatusIntervalId: null,
      gift: {
        code: '',
        robux: 0,
        instruction: 0
      },
      currentTab: 'transfer'
    }
  },
  created() {
    this.$store.dispatch('authorization', this.$cookies.get('access_token'))
    this.loading = true

    this.checkRouteParams()
    // Заказ пака, оплаченный но не выданный, требует действия пользователя —
    // проверяем сразу (например, при возврате с платёжки), на любом табе.
    if (this.currentTab !== 'packs') this.getPacksHistory(1, { silent: true })
    this.getKeysHistory()
    const requestHeaders = new Headers({
      Authorization: `Bearer ${this.$cookies.get('access_token')}`
    })

    const requestParams = {
      method: 'GET',
      headers: requestHeaders
    }

    fetch('https://transfer.backend-pier.com/get_history?count=7&page=1', requestParams)
      .then(res => res.json())
      .then(data => {
        if (data.status) {
          this.totalPages.transfer = data.total_pages
          this.totalOrders.transfer = data.total_orders
          this.orders = data.orders
          // this.orders.forEach((order) => {
          //   const orderDate = new Date(order.date)
          //   orderDate.setHours(orderDate.getHours() + 1)
          //   const delta = orderDate - Date.now()

          //   this.timers.push({ [order._id]: delta })
          // })

          // this.initTimers()
        }

        this.loading = false
      })
      .catch(err => console.log(err))
  },
  computed: {
    PACK_STATUS: () => PACK_STATUS,
    currentPaginationPages() {
      const indexes = []
      const maxDisplayedIndexes = 5
      const halfDisplayedIndexes = Math.floor(maxDisplayedIndexes / 2)

      let start = Math.max(this.currentPage - halfDisplayedIndexes, 1)
      const end = Math.min(start + maxDisplayedIndexes - 1, this.totalPages[this.currentTab])

      if (end - start < maxDisplayedIndexes - 1) {
        start = Math.max(end - maxDisplayedIndexes + 1, 1)
      }

      for (let i = start; i <= end; i++) {
        indexes.push(i)
      }

      return indexes
    },
    showEllipsisBefore() {
      return (
        (this.currentPaginationPages[0] > 1 || this.totalPages[this.currentTab] > 5) &&
        this.currentPage < this.totalPages[this.currentTab] - 2
      )
    },
    showLastPage() {
      return this.currentPaginationPages[this.currentPaginationPages.length - 1] < this.totalPages[this.currentTab]
    },
    currentOrders() {
      if (this.isGifts) {
        return this.giftOrders
      }
      return this.orders
    },
    isGifts() {
      return this.currentTab === 'gifts'
    },
    isBaseTab() {
      return this.currentTab === 'transfer'
    },
    combinedGiftOrders() {
      const gifts = this.giftOrders.map(o => ({ ...o, _orderType: 'gift' }))
      const keys = this.keyOrders.map(o => ({ ...o, _orderType: 'key' }))
      return [...gifts, ...keys].slice(0, 7)
    },
    giftTitle() {
      if (this.screenWidth < ScreenSize.MOBILE_LG) return this.$t('gift.name')
      return this.$t('general.gifts')
    }
  },
  watch: {
    '$store.state.account.userId': {
      handler(value) {
        if (value) this.getPassHistory()
      },
      immediate: true
    }
  },
  methods: {
    getPromoBonus,
    copyId(text) {
      copyText(text)
    },
    getOrderTotal(order) {
      return order.amount[order.currency.toLowerCase()].toFixed(2)
    },
    getOrderDescription(order) {
      const code = order.code_description
      const isSelfDescription = CODE_WITH_SELF_DESCRIPTION.includes(code)

      if (order.status === 'error') {
        return isSelfDescription ? order.description : this.$t('order.descriptions.error')
      }

      const texts = {
        0: this.$t('order.descriptions.wait'),
        1: this.$t('order.descriptions.notEnoughFunds'),
        2: this.$t('order.descriptions.systemError'),
        3: this.$t('order.descriptions.checkVip'),
        4: this.$t('order.descriptions.connectionError'),
        5: this.$t('order.descriptions.success'),
        6: this.$t('order.descriptions.checkGamepass')
      }

      return texts[code]
    },
    getOrderPassCancelReason(reason) {
      const texts = {
        noJoin: this.$t('order.cancelReason.noJoin'),
        noPlay: this.$t('order.cancelReason.noPlay'),
        noItem: this.$t('order.cancelReason.other'),
        alreadyBought: this.$t('order.cancelReason.alreadyBought'),
        gameError: this.$t('order.cancelReason.gameError'),
        serverError: this.$t('order.cancelReason.serverError'),
        timeout: this.$t('order.cancelReason.other'),
        other: this.$t('order.cancelReason.other')
      }

      return texts[reason] || texts.other
    },
    getOrderPassDescription(order) {
      const code = order.status
      const reason = order.cancel_reason

      const texts = {
        in_game: this.$t('order.descriptions.passInGame'),
        success: this.$t('order.descriptions.passSuccess'),
        paid: this.$t('order.descriptions.passWaiting'),
        cancel: this.getOrderPassCancelReason(reason),
        error: this.getOrderPassCancelReason(reason)
      }

      return texts[code]
    },
    changeOrders(page, tab) {
      this.ordersLoading = true
      this.currentPage = page

      const requestHeaders = new Headers({
        Authorization: `Bearer ${this.$cookies.get('access_token')}`
      })

      const requestParams = {
        method: 'GET',
        headers: requestHeaders
      }

      if (tab === 'gifts') {
        this.getKeysHistory(page)
        return
      }

      if (tab === 'superPass') {
        if (!this.passOrders.length || page) this.getPassHistory(page)
        return
      }

      if (tab === 'packs') {
        this.getPacksHistory(page)
        return
      }

      fetch(`https://transfer.backend-pier.com/get_history?count=7&page=${page}`, requestParams)
        .then(res => res.json())
        .then(data => {
          if (data.status) {
            this.timers = []
            this.totalPages.transfer = data.total_pages
            this.totalOrders.transfer = data.total_orders
            this.orders = data.orders

            // this.orders.forEach((order) => {
            //   const orderDate = new Date(order.date)
            //   orderDate.setHours(orderDate.getHours() + 1)
            //   const delta = orderDate - Date.now()

            //   this.timers.push({ [order._id]: delta })
            // })

            // this.initTimers()

            this.ordersLoading = false
          } else {
            this.ordersLoading = false
          }
        })
        .catch(err => console.log(err))
    },
    getGiftsHistory(page = 1) {
      const requestHeaders = new Headers({
        Authorization: `Bearer ${this.$cookies.get('access_token')}`
      })

      const requestParams = {
        method: 'GET',
        headers: requestHeaders
      }

      fetch(`https://transfer.backend-pier.com/get_history_gifts?count=7&page=${page}`, requestParams)
        .then(res => res.json())
        .then(data => {
          if (data.status) {
            this.timers = []
            this.giftOrders = data.orders
            this.totalPages.gifts = data.total_pages
            this.totalOrders.gifts = data.total_orders

            this.ordersLoading = false
          } else {
            this.ordersLoading = false
          }
        })
        .catch(err => console.log(err))
    },
    getKeysHistory(page = 1) {
      const requestHeaders = new Headers({
        Authorization: `Bearer ${this.$cookies.get('access_token')}`
      })

      fetch(`https://transfer.backend-pier.com/get_keys_history?count=7&page=${page}`, {
        method: 'GET',
        headers: requestHeaders
      })
        .then(res => res.json())
        .then(data => {
          if (data.status) {
            this.keyOrders = data.orders
            this.totalOrders.gifts = data.total_orders
            this.totalPages.gifts = data.total_pages

            this.ordersLoading = false

            const TWO_MINUTES = 2 * 60 * 1000
            const recentSuccessKey = data.orders.find(order => {
              if (order.status !== 'success') return false
              return Date.now() - new Date(order.date).getTime() < TWO_MINUTES
            })
            if (recentSuccessKey) this.showKeyModal(recentSuccessKey)
          } else {
            this.ordersLoading = false
          }
        })
        .catch(err => console.log(err))
    },
    getPassHistory(page = 1) {
      const requestHeaders = new Headers({
        Authorization: `Bearer ${this.$cookies.get('access_token')}`
      })
      const userId = this.$store.state.account.userId

      const requestParams = {
        method: 'GET',
        headers: requestHeaders
      }

      fetch(
        `https://transfer.backend-pier.com/get_superpasses_history?count=7&page=${page}&user_id=${userId}`,
        requestParams
      )
        .then(res => res.json())
        .then(data => {
          if (data.status) {
            this.timers = []
            this.passOrders = data.orders
            this.totalPages.superPass = data.total_pages
            this.totalOrders.superPass = data.total_orders

            this.ordersLoading = false
            this.checkActivePassOrderInHistory()
          } else {
            this.ordersLoading = false
          }
        })
        .catch(err => console.log(err))
    },
    checkActivePassOrderInHistory() {
      const activeStatuses = ['paid', 'in_game']

      this.passOrders.forEach(async item => {
        // Отображение таймера активного пасса
        if (activeStatuses.includes(item.status)) {
          if (!this.games.length) await this.getGames()

          const orderGame = this.games.find(game => game.id === item.game_id)
          // Если не требует захода на сервер - пропускаем
          if (!orderGame?.in_game) return

          this.currentPassOrder = item
          this.startCheckOrderPassStatus()
        }
      })
    },
    async checkActivePassOrder() {
      const requestHeaders = new Headers({
        Authorization: `Bearer ${this.$cookies.get('access_token')}`
      })

      const requestParams = {
        method: 'GET',
        headers: requestHeaders
      }

      fetch('https://transfer.backend-pier.com/active_superpass_orders_new', requestParams)
        .then(res => res.json())
        .then(data => {
          if (data.orders?.length) {
            this.currentPassOrder = data.orders[0]
            return
          }
          // Сброс активного заказа пасса
          if (this.currentPassOrder) {
            this.currentPassOrder = null
            clearInterval(this.passStatusIntervalId)
            window.location.reload()
          }
        })
        .catch(err => console.log(err))
    },
    startCheckOrderPassStatus() {
      this.passStatusIntervalId = setInterval(this.checkActivePassOrder, 15000)
    },
    initTimers() {
      this.timers.forEach(timer => {
        const key = Object.entries(timer)[0][0]

        const iid = setInterval(() => {
          timer[key] -= 1000

          if (timer[key] <= 0) {
            clearInterval(iid)
          }
        }, 1000)
      })
    },
    getTimer(id) {
      const rawTimer = this.timers.find(t => Object.keys(t)[0] === id)
      const rawDelta = Object.values(rawTimer)[0]

      if (rawDelta <= 0) {
        return '00:00:00'
      } else {
        const formatedHours = ('0' + String(Math.trunc(rawDelta / 1000 / 60 / 60))).slice(-2)
        const formatedMinutes = ('0' + String(Math.trunc((rawDelta / 1000 / 60) % 60))).slice(-2)
        const formatedSeconds = ('0' + String(Math.trunc(((rawDelta / 1000) % 60) % 60))).slice(-2)

        return `${formatedHours}:${formatedMinutes}:${formatedSeconds}`
      }
    },
    parseDate(id, orders) {
      const foundOrder = this[orders].find(order => order._id === id)
      const date = new Date(foundOrder?.finish_date || foundOrder?.date)

      const day = ('0' + date.getDate()).substr(-2)
      const month = ('0' + (date.getMonth() + 1)).substr(-2)
      const year = date.getFullYear()
      const hour = ('0' + date.getHours()).substr(-2)
      const minutes = ('0' + date.getMinutes()).substr(-2)

      return `${day}.${month}.${year} ${hour}:${minutes}`
    },
    goToTransactions() {
      window.open('https://www.roblox.com/transactions', '_blank')
    },
    showKeyModal(order) {
      this.gift.code = order.key
      this.gift.robux = order.quantity
      this.gift.instruction = order.instruction
      this.isShowGiftModal = true
    },
    showGiftModal(order) {
      this.gift.code = order.gift_code || order.code
      this.gift.robux = order.robux_amount
      this.gift.instruction = order.instruction

      this.isShowGiftModal = true
    },
    setTab(tab) {
      this.currentTab = tab

      if (tab !== 'packs') this.stopPacksPolling()

      if (tab === 'superPass') {
        this.getGames()
        this.getPasses()
        if (!this.passOrders.length) this.getPassHistory()
        return
      }

      if (tab === 'gifts') {
        this.getKeysHistory()
      }

      if (tab === 'packs') {
        this.getPacksHistory()
      }
    },
    async getPacksHistory(page = 1, { silent = false } = {}) {
      if (!silent) this.ordersLoading = true

      const data = await getPacksHistory({
        token: this.$cookies.get('access_token'),
        page
      })

      if (data.status) {
        this.packOrders = data.orders
        this.totalPages.packs = data.total_pages
        this.totalOrders.packs = data.total_orders

        this.checkPacksVerification()
        this.openPendingPackOrder()
        this.startPacksPolling()
      }

      if (!silent) this.ordersLoading = false
    },
    // Опрашиваем статус, пока есть незавершённые заказы: их выполняет провайдер,
    // статус меняется по колбэку уже после того, как мы отдали креды.
    startPacksPolling() {
      const hasActive = this.packOrders.some(order => PACK_ACTIVE_STATUSES.includes(order.status))

      if (!hasActive || this.currentTab !== 'packs') {
        this.stopPacksPolling()
        return
      }

      if (this.packsStatusIntervalId) return

      this.packsStatusIntervalId = setInterval(() => {
        if (this.currentTab !== 'packs') {
          this.stopPacksPolling()
          return
        }

        this.getPacksHistory(this.currentPage, { silent: true })
      }, PACKS_POLL_INTERVAL)
    },
    stopPacksPolling() {
      clearInterval(this.packsStatusIntervalId)
      this.packsStatusIntervalId = null
    },
    // Модалку выдачи открываем для заказа из query (сразу после оплаты) либо
    // для любого оплаченного заказа, по которому ещё не передали данные аккаунта.
    openPendingPackOrder() {
      // Модалка проверки приоритетнее — визард поверх неё не открываем.
      if (this.activePackOrder || this.isShowPacksProcessing || this.isShowPacksVerification) return

      const orderId = this.$route.query.order
      const waiting = this.packOrders.filter(isPackWaitingCredentials)
      const pending = orderId ? waiting.find(order => order._id === orderId) : waiting[0]

      if (!pending) return

      // Деньги уже списаны, а данных аккаунта нет — сразу показываем нужный таб
      // (без setTab, чтобы не перезапрашивать только что полученную историю).
      this.currentTab = 'packs'
      this.openPacksWizard(pending)
    },
    // Провайдер запросил проверку — без неё заказ не выполнится, поэтому
    // открываем модалку сами, не дожидаясь клика по строке истории.
    checkPacksVerification() {
      const order = this.packOrders.find(item => item.status === PACK_STATUS.VERIFICATION)

      // Проверку прошли (или заказ отменился) — держать модалку не за чем.
      if (!order) {
        if (this.isShowPacksVerification) this.onPacksVerificationClose()
        return
      }

      // Обновляем данные открытой модалки: verification_url мог появиться позже.
      if (this.isShowPacksVerification) this.packVerificationOrder = order

      if (!order.verification_url || this.closedVerifications.includes(order._id)) return

      this.openPackVerification(order)
    },
    // Проверка приоритетнее визарда выдачи и «Обработки заказа»: пока её не
    // пройдут, заказ не двинется.
    openPackVerification(order) {
      this.isShowPacksWizard = false
      this.isShowPacksProcessing = false
      this.packVerificationOrder = order
      this.isShowPacksVerification = true
      // Заказ требует действия — за модалкой должен быть нужный таб.
      this.currentTab = 'packs'
    },
    onPacksVerificationClose() {
      this.isShowPacksVerification = false

      if (this.packVerificationOrder) this.closedVerifications.push(this.packVerificationOrder._id)
    },
    isPackWaitingCredentials,
    isPackError(order) {
      return [PACK_STATUS.CANCEL, 'error', 'system_error'].includes(order.status)
    },
    showPackCancelReason(order) {
      if (!order.cancel_reason) return

      this.$store.dispatch('notification/setNotification', {
        title: this.$t('history.error'),
        description: order.cancel_reason,
        type: 'error'
      })
    },
    getPackProductName(order) {
      const name = this.$store.getters.language === 'ru' ? order.product_name : order.product_name_en

      return name || order.product_name || (order.robux ? `${order.robux} R$` : '—')
    },
    // Паки продаются только в рублях — сумма всегда в ₽.
    getPackAmount(order) {
      const amount = Number(order.rub_amount || order.amount?.rub) || 0

      return amount ? amount.toFixed(2) : '—'
    },
    openPacksWizard(order) {
      this.activePackOrder = order
      this.isShowPacksWizard = true
    },
    onPacksWizardDone() {
      this.isShowPacksWizard = false
      this.isShowPacksProcessing = true
      // Креды отданы — дальше заказ ведёт воркер, следим за статусом.
      this.getPacksHistory(this.currentPage, { silent: true })
    },
    onPacksProcessingClose() {
      this.isShowPacksProcessing = false
      this.activePackOrder = null
      this.getPacksHistory(this.currentPage)
    },
    getGameInfo(order) {
      const game = this.games.find(game => game.id === order?.game_id)
      if (!game) return {}
      const pass = this.passes.find(pass => pass.id === order?.superpass_id)

      return {
        gameName: game?.name,
        passName: pass?.name
      }
    },
    async getGames() {
      await fetch('https://transfer.backend-pier.com/games_new')
        .then(res => res.json())
        .then(data => {
          if (data.games) {
            this.games = data.games.filter(game => game.superpass_enabled)
          }
        })
        .catch(err => console.log(err))
        .finally(() => {})
    },
    async getPasses() {
      await fetch('https://transfer.backend-pier.com/sp_new')
        .then(res => res.json())
        .then(data => {
          if (data.superpasses) {
            this.passes = data.superpasses
          }
        })
        .catch(err => console.log(err))
        .finally(() => {})
    },
    checkRouteParams() {
      const method = this.$route.query.method
      if (method) {
        this.setTab(method)
      }
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }
  },
  beforeDestroy() {
    clearInterval(this.passStatusIntervalId)
    this.stopPacksPolling()
  }
}
</script>
